<script lang="ts" setup>
interface SeoMeta {
  title?: string
  description?: string
  image?: string
}

interface Props {
  title?: string
  seo?: SeoMeta
}

defineOptions({
  inheritAttrs: false,
})

const props = defineProps<Props>()

const route = useRoute()
const runtimeConfig = useRuntimeConfig()
const { appFrontEndDomain } = runtimeConfig.public
const expanded = useNavExpanded()
expanded.value = 'responsive'

const pageTitle = computed(() => (route.meta.title as string | undefined) || props.title)

const defaultSeo = {
  title: 'Forgd: A new kind of Web3 investment bank',
  description: 'Forgd provides blockchain projects with free tools and solutions for token launch and protocol optimization—all in a single platform.',
}

const seoTitle = computed(() => props.seo?.title || defaultSeo.title)
const seoDescription = computed(() => props.seo?.description || defaultSeo.description)
const defaultSeoImage = 'https://cdn.prod.website-files.com/6642268ef69d551ef8d67770/66d72c1ba054de8cd491c239_opengraph-forgd.jpg'
const seoImage = computed(() => props.seo?.image || defaultSeoImage)
const { isContentSearchModalOpen } = useUIState()

useHead({
  title: pageTitle,
  htmlAttrs: {
    class: 'scroll-smooth',
  },
})

useSeoMeta({
  description: seoDescription,
  ogTitle: seoTitle,
  ogDescription: seoDescription,
  ogImage: seoImage,
  ogImageType: 'image/jpeg',
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogType: 'website',
  ogSiteName: 'Forgd',
  ogUrl: () => `${appFrontEndDomain}${route.path}`,
  twitterCard: 'summary_large_image',
  twitterTitle: seoTitle,
  twitterDescription: seoDescription,
  twitterImage: seoImage,
})

const searchTerm = ref('')
const { searchGroups } = useAppSearch(searchTerm)

onMounted(() => {
  const getScrollOffset = () =>
    window.matchMedia('(max-width: 767px)').matches ? 116 : 40

  const scrollToHash = (hash: string) => {
    if (hash) {
      const element = document.getElementById(hash.replace('#', ''))
      if (element) {
        const elementPosition = element.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.scrollY - getScrollOffset()

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        })
      }
    }
  }

  watch(() => route.hash, scrollToHash, { immediate: true })
})
</script>

<template>
  <div
    data-layout="homepage"
    class="flex min-h-screen flex-col bg-neutral-200 font-display font-normal text-primal"
  >
    <!-- rail + body: same height, end together above the footer -->
    <div class="flex min-w-0 grow">
      <AppNav />
      <main class="flex min-w-0 grow flex-col" data-inertable>
        <CorePageHeaderMobile />
        <!-- page header -->
        <div
          class="relative hidden h-20 items-center justify-between border-b border-default px-5 md:flex"
        >
          <slot name="header">
            <CorePageHeader />
            <ClientOnly>
              <LazyCoreSearchModal v-model="searchTerm" v-model:is-open="isContentSearchModalOpen" :groups="searchGroups" />
            </ClientOnly>
          </slot>
        </div>

        <slot name="subheader" />
        <div class="flex min-w-0 grow flex-col">
          <slot />
        </div>
      </main>
    </div>

    <!-- full-width footer: bleeds under the rail -->
    <footer class="w-full">
      <slot name="footer" />
      <CoreFooter />
    </footer>

    <AuthLoginModal />
  </div>
</template>
